import React, { useState } from "react";
import Swal from "sweetalert2";
import { getDailyPatientReport, getMonthlyDailyCountReport } from "../../core/api/reportService";
import { getPatients } from "../../core/api/patientService";
import { getProcedures } from "../../core/api/procedureService";
import StickyScrollTable from "../components/StickyScrollTable";
import { exportToCsv } from "../../core/utils/exportCsv";
import { getApiErrorMessage } from "../../core/utils/apiError";
import { REPORT_CATEGORIES, categoryOf } from "../../core/constants/reportCategories";

const thisMonth = () => new Date().toISOString().slice(0, 7);
const pad = (n) => String(n).padStart(2, "0");
const displayDate = (iso) => { const [y, m, d] = iso.split("-"); return `${d}/${m}/${y}`; };
const norm = (s) => String(s || "").trim().toLowerCase();
const KEY_BY_CATEGORY = new Map(REPORT_CATEGORIES.map((c) => [c.value, c.key]));

// Runs `fn` over `items` a few at a time, so a month doesn't fire 31 requests at once.
const mapLimited = async (items, limit, fn) => {
  const out = new Array(items.length);
  let next = 0;
  const worker = async () => { while (next < items.length) { const i = next++; out[i] = await fn(items[i]); } };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return out;
};

// One day's counts from that day's /api/reports/daily-patient rows (one row per invoice,
// `treatment` = comma-joined procedure names). Rows with no treatment are medicine-only bills
// and aren't counted.
const countDay = (date, dayRows, newOP, categoryByName) => {
  const patientsByKey = new Map(REPORT_CATEGORIES.map((c) => [c.key, new Set()]));
  const allPatients = new Set();
  dayRows.forEach((r) => {
    const names = String(r.treatment || "").split(",").map(norm).filter(Boolean);
    if (names.length === 0) return;
    const patient = norm(r.patientID) || `invoice-${r.invoiceID ?? r.slNo}`;
    allPatients.add(patient);
    names.forEach((n) => {
      const key = KEY_BY_CATEGORY.get(categoryByName.get(n));
      if (key) patientsByKey.get(key).add(patient);
    });
  });
  return {
    date,
    newOP,
    ...Object.fromEntries([...patientsByKey].map(([k, set]) => [k, set.size])),
    totalPatients: allPatients.size,
  };
};

// Count columns in display order; New OP and Total bracket the procedure categories.
const COLUMNS = [
  { key: "newOP", label: "New OP" },
  ...REPORT_CATEGORIES.map(({ key, label }) => ({ key, label })),
  { key: "totalPatients", label: "Total No. of Patients" },
];

// Interim, until GET /api/reports/monthly-daily-count ships: built from existing endpoints —
// New OP from the patients' createdDate, and the treatment counts from one daily-patient call
// per day, with each procedure name mapped to its report category via the Procedure master.
// Ortho here counts every ortho visit, not only a patient's first.
const buildFromExistingApis = async (dates) => {
  const [patientsRes, proceduresRes, dayResults] = await Promise.all([
    getPatients(),
    getProcedures(),
    mapLimited(dates, 6, (d) => getDailyPatientReport(d).then((r) => r.data || [])),
  ]);

  const newOPByDate = new Map();
  (patientsRes.data || []).forEach((p) => {
    const d = String(p.createdDate || "").split("T")[0];
    if (d) newOPByDate.set(d, (newOPByDate.get(d) || 0) + 1);
  });
  const categoryByName = new Map((proceduresRes.data || []).map((p) => [norm(p.procedureName), categoryOf(p)]));

  return dates.map((d, i) => countDay(d, dayResults[i], newOPByDate.get(d) || 0, categoryByName));
};

// GET /api/reports/monthly-daily-count may omit days with no activity — list every day of the
// month, with zeros, so the sheet is a full month.
const fillMonth = (dates, apiRows) => {
  const byDate = new Map(apiRows.map((r) => [String(r.date || "").split("T")[0], r]));
  return dates.map((date) => {
    const r = byDate.get(date) || {};
    return { date, ...Object.fromEntries(COLUMNS.map((c) => [c.key, Number(r[c.key] || 0)])) };
  });
};

const MonthlyCountReport = () => {
  const [month, setMonth] = useState(thisMonth());
  const [rows, setRows] = useState(null);
  const [estimated, setEstimated] = useState(false);
  const [loading, setLoading] = useState(false);

  const runReport = async () => {
    if (!month) return;
    const [year, m] = month.split("-").map(Number);
    const dates = Array.from({ length: new Date(year, m, 0).getDate() }, (_, i) => `${year}-${pad(m)}-${pad(i + 1)}`);
    setLoading(true);
    try {
      try {
        const res = await getMonthlyDailyCountReport({ year, month: m });
        setRows(fillMonth(dates, res.data || []));
        setEstimated(false);
      } catch (err) {
        // 404 = the endpoint hasn't shipped yet; anything else is a real error.
        if (err?.response?.status !== 404) throw err;
        setRows(await buildFromExistingApis(dates));
        setEstimated(true);
      }
    } catch (err) {
      Swal.fire("Error", getApiErrorMessage(err, "Failed to load the monthly report."), "error");
    } finally {
      setLoading(false);
    }
  };

  const totals = Object.fromEntries(COLUMNS.map((c) => [c.key, (rows || []).reduce((s, r) => s + r[c.key], 0)]));

  const handleExport = () => {
    const headers = ["Date", ...COLUMNS.map((c) => c.label)];
    const dataRows = rows.map((r) => [displayDate(r.date), ...COLUMNS.map((c) => r[c.key])]);
    dataRows.push(["TOTAL", ...COLUMNS.map((c) => totals[c.key])]);
    exportToCsv(`Monthly-Report-${month}.csv`, headers, dataRows);
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="page-title"><h4>Monthly Report</h4><h6>Day-wise patient count by treatment</h6></div>
        </div>

        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-2 align-items-end">
              <div className="col-auto">
                <label className="form-label small mb-1">Month</label>
                <input type="month" className="form-control form-control-sm" value={month} onChange={(e) => setMonth(e.target.value)} />
              </div>
              <div className="col-auto">
                <button className="btn btn-primary" onClick={runReport} disabled={loading || !month}>
                  {loading ? "Loading..." : "Search"}
                </button>
              </div>
              <div className="col-auto">
                <button className="btn btn-outline-success" onClick={handleExport} disabled={!rows || rows.length === 0}>
                  Export to Excel
                </button>
              </div>
            </div>
          </div>
        </div>

        {rows && estimated && (
          <div className="alert alert-warning py-2 small">
            Treatment counts are matched from procedure names, and New Ortho Cases includes repeat ortho visits.
            Exact figures need the backend monthly report.
          </div>
        )}

        {rows && (
          <div className="card">
            <div className="card-body p-0">
              <StickyScrollTable>
                <table className="table table-sm table-bordered mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Date</th>
                      {COLUMNS.map((c) => <th key={c.key} className="text-center">{c.label}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.date}>
                        <td className="text-nowrap">{displayDate(r.date)}</td>
                        {COLUMNS.map((c) => (
                          <td key={c.key} className={`text-center${c.key === "totalPatients" ? " fw-semibold" : ""}`}>
                            {r[c.key] || "-"}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="table-light fw-bold">
                      <td>TOTAL</td>
                      {COLUMNS.map((c) => <td key={c.key} className="text-center">{totals[c.key]}</td>)}
                    </tr>
                  </tfoot>
                </table>
              </StickyScrollTable>
            </div>
          </div>
        )}

        {!rows && !loading && (
          <div className="card text-center py-5 text-muted">
            <p>Select a month and click <strong>Search</strong> to view the report.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MonthlyCountReport;
